import * as THREE from 'three';
import type { LayerHandle } from '@/hero/GlbLayer';
import { MODELS_BASE } from '@/hero/preload';
import { hero, useTourStore } from '@/state/tour';
import { useHeroStore } from '@/store/store';
import { layersOf, resetLayers, type StationImpl } from '../types';
import { DrawingsPanel } from './Panel';
import { PLAN_CUT, sectionAt, syncDrawing } from './request';
import { useDrawingsStore } from './state';

// per second, how fast the cut follows a storey or slider change
const EASE = 5;

// the hero in its own model frame (MeshData and GLB share it), and that frame to world
const box = new THREE.Box3();
const toWorld = new THREE.Matrix4();
const point = new THREE.Vector3();
const normal = new THREE.Vector3();
const plane = new THREE.Plane();
let clipY = 0;
let at = 0.5;
let hovered: string | null = null;
let highlighted: number | null = null;

export const drawings: StationImpl = {
  pick: 'none',
  enter({ layers, height }) {
    resetLayers(layers, height);
    for (const h of layersOf(layers, (d) => d === 'space')) h.rig.setVisible(true);
    for (const h of layersOf(layers, (d) => d === 'mep')) h.rig.setVisible(false);
    box.makeEmpty();
    for (const h of layers.values()) box.union(h.box);
    clipY = height + 2;
    at = useDrawingsStore.getState().at;
    hovered = null;
    highlighted = null;
    if (hero.ifc) useHeroStore.getState().load(MODELS_BASE + hero.ifc);
  },
  update({ layers, height, dt }) {
    if (box.isEmpty() || !modelToWorld(layers)) return;
    syncDrawing(box);
    const s = useDrawingsStore.getState();
    const snap = useTourStore.getState().reducedMotion;
    const k = snap ? 1 : 1 - Math.exp(-dt * EASE);

    let targetY = height + 2;
    if (s.mode === 'plan') {
      const storey = useHeroStore.getState().storeys.find((x) => x.expressID === s.storeyId);
      if (storey)
        targetY = point.set(0, (storey.elevation ?? 0) + PLAN_CUT, 0).applyMatrix4(toWorld).y;
    }
    clipY += (targetY - clipY) * k;
    at += (s.at - at) * k;

    const cut = s.mode === 'section' ? sectionPlane(s.axis, at) : null;
    for (const h of layers.values()) {
      h.rig.setClipY(clipY);
      h.rig.setClipPlane(cut);
    }
    highlight(layers, s.hoveredRoom);
  },
  leave({ layers, height }) {
    for (const h of layers.values()) h.rig.setClipPlane(null);
    resetLayers(layers, height);
    hovered = null;
    highlighted = null;
    useDrawingsStore.getState().hoverRoom(null);
  },
  Panel: DrawingsPanel,
};

// Clips the side the section does not look at, the same half the drawing leaves out.
function sectionPlane(axis: 'x' | 'z', at: number): THREE.Plane {
  const { origin, normal: n } = sectionAt(box, axis, at);
  normal.set(-n[0], -n[1], -n[2]);
  return plane.setFromNormalAndCoplanarPoint(normal, point.set(...origin)).applyMatrix4(toWorld);
}

// Every batch sits in the hero's layer group, so any one of them carries the model-to-world move.
function modelToWorld(layers: Map<string, LayerHandle>): boolean {
  for (const h of layers.values())
    for (const el of h.rig.elements.values()) {
      const batch = el.instances[0]?.batch;
      if (!batch) continue;
      batch.updateWorldMatrix(true, false);
      toWorld.copy(batch.matrixWorld);
      return true;
    }
  return false;
}

function highlight(layers: Map<string, LayerHandle>, globalId: string | null) {
  if (globalId === hovered) return;
  hovered = globalId;
  const id =
    globalId == null
      ? null
      : (useHeroStore.getState().elements.find((e) => e.globalId === globalId)?.expressID ?? null);
  for (const h of layers.values()) {
    if (highlighted != null) h.rig.setElementState(highlighted, null);
    if (id != null) h.rig.setElementState(id, 'highlight');
  }
  highlighted = id;
}
